import { takeUntil, throttleTime } from "rxjs/operators";
import { Subject } from "rxjs";
import {
  Component,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ElementRef,
  QueryList,
  ViewChildren,
  AfterViewInit,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { ApiService } from "../../../services/api.service";
import { ActivatedRoute } from "@angular/router";
import { Location, NgClass } from "@angular/common";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { AnimationCollapse } from "../../../animations";
import { NgxBootstrapMultiselectModule, IMultiSelectOption, IMultiSelectSettings, IMultiSelectTexts } from 'ngx-bootstrap-multiselect';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Device, DeviceReplacementStatus, ReplaceDeviceForm } from "../../../models/device.model";
import { HelperService } from "../../../services/helper.service";
import { MatTimepickerModule } from "@angular/material/timepicker";
import { MAT_DATE_FORMATS, MatOptionModule } from "@angular/material/core";
import { dateToHhmm, hhmmToDate, TIMEPICKER_24H_FORMATS } from "../../../shared/timepicker.shared";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatDialog } from "@angular/material/dialog";
import { ReplaceDeviceDialogComponent } from "./replace-device-dialog/replace-device-dialog.component";
import { ReplaceActivationWarningDialogComponent } from "./replace-activation-warning-dialog/replace-activation-warning-dialog.component";
import { OverrideDoorIdDialogComponent } from "./override-door-id-dialog/override-door-id-dialog.component";

type DeviceReplacementChart = { [devicePrefix: string]: string[] };

// TODO: add more device replacement rules when needed
const deviceReplacementChart: DeviceReplacementChart = {
  /** PL-100 button */
  "04": ["05"],
  /**
   * PL-120A/AS (10-5) → PL-120CA (17-1)
   * PL-120AN (10-6) → PL-120CA (17-1)
   * PL-125B  (10-3) → PL-125CA (17-5)
   * PL-105B2 (10-6) → PL-105CA (15-1)
   */
  "10": ["17", "15"],
  /**
   * PL-105 variants (13-1) → PL-105CA (15-1)
   */
  "13": ["15"],
}

/**
 * Activation types that exist on PL-120Ax (10-5 / 10-6) but are absent in PL-120CA (17-1).
 * Any alarm routing using these group/node combinations will be permanently lost on replacement.
 */
const PL120_INCOMPATIBLE_ACTIVATIONS: { group: number; node: number }[] = [
  { group: 1, node: 16 }, // SABO Switch
  { group: 1, node: 32 }, // Doormat
  { group: 2, node: 4  }, // Traffic outwards
  { group: 2, node: 5  }, // Traffic inwards
];

@Component({
    selector: "device-control",
    templateUrl: "./device-control.component.html",
    styleUrls: ["./device-control.component.css"],
    animations: [AnimationCollapse],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [NgxBootstrapMultiselectModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule, NgClass, MatTimepickerModule, TranslateModule],
    providers: [{ provide: MAT_DATE_FORMATS, useValue: TIMEPICKER_24H_FORMATS }]
})
export class DeviceControlComponent
  implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  //Init global variables and services
  constructor(
    private api: ApiService,
    private helper: HelperService,
    private route: ActivatedRoute,
    private location: Location,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
  ) { }

  @ViewChildren("ctrlElement") ctrlElements: QueryList<ElementRef>;

  @Input() device: Device;
  @Input() pairedDevices: Device[];
  @Input() basestationList: Device[];

  @Output() deviceDeleted = new EventEmitter<string>();
  @Output() cancelDeviceDelete = new EventEmitter<string>();
  @Output() deviceForceDeleted = new EventEmitter<Device>();
  @Output() deviceReplaced = new EventEmitter<string>();
  @Output() settingsTransferred = new EventEmitter<number>();
  @Output() updateError = new EventEmitter<boolean>();
  @Output() settingTransferCancelled = new EventEmitter<boolean>();
  @Output() nfcMoved = new EventEmitter<[string, string]>();
  @Output() doorIdOverridden = new EventEmitter<void>();
  @Output() isDeviceManagementEmpty: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  deleting: boolean = false;
  cancelling: boolean = false;
  forceDeleting: boolean = false;
  replaceError: string;
  replaceDevice: string;
  isReplaced: boolean;
  doorIdOverride: string;
  currentDoorId: string;
  doorIdRotationActive: boolean = false;
  overrideDoorIdError: string;
  overrideDoorIdSuccess: boolean = false;
  user: any;
  firmwareVersions: any[];
  otaUpdateForm: UntypedFormGroup;
  otaError: boolean = false;
  otaSuccess: boolean = false;
  toBeDeleted: boolean = false;
  nfcMoveSuccess: boolean = false;
  nfcMoveError: boolean = false;
  deviceInventoryEnabled: boolean = false;

  formSubmitSubject = new Subject<void>();
  private destroy$ = new Subject<void>();

  basestationSelect: IBaseDeviceSelect = {
    options: [],
    texts: {},
    settings: {
      enableSearch: true,
      closeOnSelect: true,
      checkedStyle: "fontawesome",
      buttonClasses: "btn graySSBtn",
      selectionLimit: 1,
      autoUnselect: true,
      dynamicTitleMaxItems: 1,
      maxHeight: "510px",
    },
  };
  selectedBasestation: Device[] = [];

  moveNfcTag(): void {
    if (this.user.roleLevel >= 200 && this.device.deviceType === "08-1") {
      this.nfcMoveSuccess = false;
      this.nfcMoveError = false;

      const devId = this.device.id;
      const newBaseId = this.selectedBasestation[0].id;

      this.api.moveNfcTag(devId, newBaseId).subscribe(
        (updatedBaseId: string) => {
          this.device.basestationId = updatedBaseId;
          this.nfcMoveSuccess = true;
          this.nfcMoved.emit([devId, updatedBaseId]);
        },
        (error) => {
          this.nfcMoveError = true;
          console.log(error);
        }
      );
    }
  }

  createOtaUpdateForm(): void {
    this.otaUpdateForm = this.fb.group({
      fw: ["", Validators.required],
      fwUpdateTiming:
        this.device.swUdStartTime === "00:00" &&
          this.device.swUdEndTime === "00:00"
          ? "0"
          : "1",
      fwUpdateStartTime: hhmmToDate(this.device.swUdStartTime),
      fwUpdateEndTime: hhmmToDate(this.device.swUdEndTime),
    });
  }

  deleteDevice(): void {
    this.deleting = true;
    this.isDeviceManagementEmpty.emit(false);
  }

  forceDeleteDevice(): void {
    this.forceDeleting = true;
  }

  cancelDelete(): void {
    this.deleting = false;
    this.forceDeleting = false;
  }

  revertDelete(): void {
    this.api.cancelDeleteDevice(this.device.id).subscribe(
      (res) => {
        this.cancelDeviceDelete.emit(this.device.id);
        this.device.toBeDeleted = false;
      },
      (err) => {
        this.updateError.emit(true);
      }
    );
  }

  confirmDelete(): void {
    if (this.deleting) {
      this.api.deleteDevice(this.device.id).subscribe(
        (res) => {
          this.deviceDeleted.emit(this.device.id);
          this.device.toBeDeleted = true;
        },
        (err) => {
          this.updateError.emit(true);
        }
      );
    } else if (this.forceDeleting) {
      this.api.forceDeleteDevice(this.device.id).subscribe(
        (res) => {
          this.deviceForceDeleted.emit(this.device);
        },
        (err) => {
          this.updateError.emit(true);
        }
      );
    }
  }

  getDeviceType(type: string): string {
    return this.helper.getDeviceType(type);
  }

  checkDeviceType(setting: string): boolean {
    const type = this.device.deviceType;
    switch (setting) {
      case "moveNFC":
        if (type === "08-1" && this.user.roleLevel >= 200) {
          return true;
        }
        break;
      case "rts": //Retransfer settings
        if (
          (type.substring(0, 2) === "31" && type !== "31-8") ||
          type === "36-1" ||
          type === "32-1" ||
          type === "32-3" ||
          type === "32-6" ||
          type === "32-7" ||
          type === "32-8"
        ) {
          return true;
        }
        break;
      case "replace": //Replace device
        if (type === "E8-2") {
          return false;
        }
        if (
          ((type.substring(0, 2) < "10" &&
            type !== "08-1" &&
            this.user.roleLevel >= 50)
          || type === "10-3"
          || type === "10-5"
          || type === "10-6"
          || type === "13-1"
          || type === "15-1"
          || type === "17-1"
          || type === "17-5")

          // ***** ticket EP-495
          // Replace device section is hidden for Origons

          // || ((type === "31-13" || type === "31-14") &&
          //   this.device.softwareVersion >= 47 &&
          //   this.user.roleLevel === 200)
        ) {
          return true;
        }
        break;
      case "forceDelete": //Force delete device
        //Nightowl devices will be always force deleted
        if (type === "34-4") return true;

        if (
          type.substring(0, 2) !== "31" &&
          (this.device.onlineStatus === "Offline" || this.device.toBeDeleted)
        ) {
          return true;
        }
        break;
      case "basestationDelete":
        if (type.substring(0, 2) === "31" && this.user.roleLevel < 100) {
          return false;
        }
        return true;
      case "overrideDoorId":
        // DoorID override is only available for PL-120CA (32-8) devices and only for admin
        return type === "32-8" && this.user.roleLevel >= 200 && this.doorIdRotationActive;
      case "everwatchDelete":
        // If the device is an Everwatch device and the user is not an admin, the user cannot delete the device
        return (type.substring(0, 2) !== "07" || this.user.roleLevel >= 200)
      case "nightowlDelete":
        // If the device is an Nightowl device and the user is not an admin, the user cannot delete the device
        return (type !== "34-4" || this.user.roleLevel >= 200)
    }
    return false;
  }

  reTransferSettings(): void {
    this.api.retransferSettings(this.device.id).subscribe(
      (res) => {
        this.settingsTransferred.emit(1);
      },
      (err) => {
        this.updateError.emit(true);
      }
    );
  }

  cancelSettingsTransfers(): void {
    this.api.cancelSettingTransfer(this.device.id).subscribe(
      (res) => {
        this.settingTransferCancelled.emit(true);
      },
      (err) => {
        this.updateError.emit(true);
      }
    );
  }

  isReplaceIdValid(id: string): boolean {
    const replacePrefix = id.substring(0, 2);
    const devicePrefix = this.device.id.substring(0, 2);
    const samePrefix = replacePrefix === devicePrefix;

    this.replaceError = ""

    if (!id) {
      this.replaceError = this.translate.instant("NOT_EMPTY_FIELD");
      return false;
    }

    // Check that given ID is 8 digits hexadecimal and show error if not
    if (!id.match(new RegExp("^[0-9A-F]{8}$", "i"))) {
      this.replaceError = this.translate.instant("ID_VALIDATION_INFO");
      return false;
    }

    // If the device is not replaceable with the given type and the prefix is different, show error
    if (!samePrefix && !this.isDeviceReplaceableWithDifferentType(replacePrefix)) {
      this.replaceError = this.translate.instant("DEVICE_NOT_REPLACEABLE_WITH_GIVEN_TYPE");
      return false;
    }

    return true;
  }

  /**
   * @description Checks that given replacement ID prefix can be used to replace the original device with different device type
   * @param {string} replacePrefix Replacement device ID prefix to check against
   * @returns {boolean} `true` if the device can be replaced with the given prefix, `false` otherwise
   */
  isDeviceReplaceableWithDifferentType(replacePrefix: string): boolean {
    const devicePrefix = this.device.id.substring(0, 2);

    return deviceReplacementChart[devicePrefix]
      && deviceReplacementChart[devicePrefix].indexOf(replacePrefix) !== -1;
  }

  openReplaceDialog(): void {
    const replaceId = this.replaceDevice;

    if (!this.isReplaceIdValid(replaceId)) {
      return;
    }

    this.checkIncompatibleActivations(replaceId, () => {
      const dialogRef = this.dialog.open(ReplaceDeviceDialogComponent, {
        data: new ReplaceDeviceForm(this.device.id, replaceId, this.device.name, this.device.deviceType),
        disableClose: true,
        autoFocus: false,
      });

      dialogRef.afterClosed().subscribe(
        (result: { replaced: boolean; cancelReplace?: boolean }) => {
          this.isReplaced = result.replaced;

          if (result.cancelReplace) {
            this.cancelReplace();
          } else if (result.replaced) {
            this.deviceReplaced.emit(replaceId);
          }
        });
    });
  }

  submitReplaceDevice(): void {
    this.replaceError = "";
    const replaceId = this.replaceDevice;

    if (!this.isReplaceIdValid(replaceId)) {
      return;
    }

    this.checkIncompatibleActivations(replaceId, () => {
      const replaceData = new ReplaceDeviceForm(this.device.id, replaceId, this.device.name, this.device.deviceType);
      this.api.replaceDevice(replaceData).subscribe(
      () => {
        this.deviceReplaced.emit(replaceId);
      },
      (e) => {
        this.translate
          .get([
            "INVALID_DEVICE",
            "DEVICE_IN_USE",
            "ERROR",
            "REPLACEMENT_NOT_SUPPORTED",
            "REPLACED_DEVICE_NOT_OFFLINE",
            "REPLACED_DEVICE_SOFTWARE_TOO_OLD",
            "DEVICE_REPLACE_TYPE_MISMATCH",
            "DEVICE_NOT_REPLACEABLE_WITH_GIVEN_TYPE"
          ])
          .subscribe((t) => {
            switch (e.error.status as DeviceReplacementStatus) {
              case DeviceReplacementStatus.ReplaceDeviceNotValid:
                this.replaceError = t.INVALID_DEVICE;
                break;
              case DeviceReplacementStatus.ReplaceDeviceAlreadyInUse:
                this.replaceError = t.DEVICE_IN_USE;
                break;
              case DeviceReplacementStatus.GenericError:
                this.replaceError = t.ERROR;
                break;
              case DeviceReplacementStatus.DeviceNotReplaceableWithGivenType:
                this.replaceError = t.DEVICE_NOT_REPLACEABLE_WITH_GIVEN_TYPE;
                break;
              case DeviceReplacementStatus.DeviceReplacementNotSupported:
                this.replaceError = t.REPLACEMENT_NOT_SUPPORTED;
                break;
              case DeviceReplacementStatus.ReplacedDeviceNotOffline:
                this.replaceError = t.REPLACED_DEVICE_NOT_OFFLINE;
                break;
              case DeviceReplacementStatus.ReplacedDeviceSoftwareTooOld:
                this.replaceError = t.REPLACED_DEVICE_SOFTWARE_TOO_OLD;
                break;
              default:
                this.replaceError = t.ERROR;
                break;
            }
          });
      }
    );
    });
  }

  /**
   * For PL-120Ax → PL-120CA replacements (device prefix 10, replacement prefix 17):
   * checks the device's existing activations for types that are incompatible with PL-120CA.
   * If any are found with an assigned alarm route, shows a warning dialog and only calls
   * onConfirmed after the user explicitly acknowledges. For all other replacements, calls
   * onConfirmed immediately.
   */
  checkIncompatibleActivations(replaceId: string, onConfirmed: () => void): void {
    const PL120A_SOURCE_TYPES = ["10-5", "10-6"];
    const isPL120Replacement = PL120A_SOURCE_TYPES.includes(this.device.deviceType) && replaceId.substring(0, 2) === "17";

    if (!isPL120Replacement) {
      onConfirmed();
      return;
    }

    const affectedRouteMap = new Map<string, string[]>();

    this.device.deviceActivations?.forEach(activation => {
      const incompatible = PL120_INCOMPATIBLE_ACTIVATIONS.find(
        ia => ia.group === activation.activationGroup && ia.node === activation.activationNode
      );
      if (incompatible && activation.alarmRoute?.id) {
        const routeName = activation.alarmRoute.name;
        const activationName = this.helper.getActivationName(
          activation.activationGroup,
          activation.activationNode,
          this.device.deviceType,
          activation.customActivationName,
          this.device.connectionTimeLimit
        );
        if (!affectedRouteMap.has(routeName)) {
          affectedRouteMap.set(routeName, []);
        }
        affectedRouteMap.get(routeName).push(activationName);
      }
    });

    if (affectedRouteMap.size === 0) {
      onConfirmed();
      return;
    }

    const affectedRoutes = Array.from(affectedRouteMap.entries()).map(
      ([routeName, activationNames]) => ({ routeName, activationNames })
    );

    const warningRef = this.dialog.open(ReplaceActivationWarningDialogComponent, {
      data: { affectedRoutes },
      disableClose: true,
      autoFocus: false,
    });

    warningRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        onConfirmed();
      }
    });
  }

  openOverrideDoorIdDialog(): void {
    this.overrideDoorIdError = null;
    this.overrideDoorIdSuccess = false;

    const dialogRef = this.dialog.open(OverrideDoorIdDialogComponent, {
      data: { deviceId: this.device.id },
      width: '550px',
      disableClose: true,
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.api.replaceDoorId(this.device.id, this.device.name, this.doorIdOverride, this.currentDoorId, this.user.server).subscribe({
        next: () => {
          this.overrideDoorIdSuccess = true;
          this.currentDoorId = this.doorIdOverride;
          this.doorIdOverride = null;
          // Door ID changed, let the card re-check whether it is still shared with another device
          this.doorIdOverridden.emit();
        },
        error: (err) => {
          this.overrideDoorIdError = this.translate.instant(
            err?.status === 409 ? "ROTATION_NOT_ACTIVE" : "ERROR"
          );
        }
      });
    });
  }

  cancelReplace(): void {
    if (this.isReplaced && this.device.deviceType !== "31-13") {
      this.cancelling = true;

      this.api.cancelReplace(this.device.id).subscribe(
        (res) => {
          this.deviceReplaced.emit("");
          this.replaceDevice = null;
          this.isReplaced = false;
          this.cancelling = false;
        },
        (err) => {
          this.cancelling = false;
          this.replaceError = this.translate.instant("ERROR");
          console.error(err);
        }
      );
    }
  }

  submitOta(): void {
    this.otaSuccess = false;
    this.otaError = false;

    // Convert times for 00:00 - 00:00 for immediate updates
    let updateStartTime = "00:00";
    let updateEndTime = "00:00";
    if (this.otaUpdateForm.value.fwUpdateTiming === "1") {
      updateStartTime = dateToHhmm(this.otaUpdateForm.value.fwUpdateStartTime);
      updateEndTime = dateToHhmm(this.otaUpdateForm.value.fwUpdateEndTime);
    }

    const data = {
      deviceId: this.device.id,
      firmwareId: this.otaUpdateForm.value.fw.firmwareId,
      type: this.otaUpdateForm.value.fw.swType,
      updateStartTime: updateStartTime,
      updateEndTime: updateEndTime,
    };

    this.api.startFirmwareUpdate(data).subscribe(
      (res) => {
        this.otaSuccess = true;
      },
      (err) => {
        this.otaError = true;
      }
    );
  }

  cancelOTA(): void {
    this.api.cancelFirmwareUpdate(this.device.id).subscribe(
      (res) => {
        this.device.softwareUpdateOngoing = false;
      },
      (err) => { }
    );
  }

  getBaseNameAndId(baseDev: Device) {
    return baseDev.name ? `${baseDev.name} (${baseDev.id})` : baseDev.id;
  }

  parseBaseDevOptions(basestations: Device[]): void {
    const array: IMultiSelectOption[] = [];
    for (const base of basestations) {
      array.push({ id: base, name: this.getBaseNameAndId(base) });
    }
    this.basestationSelect.options = array;
  }

  checkElementVisibility() {
    if (this.deleting || this.forceDeleting) {
      this.isDeviceManagementEmpty.emit(false);
      return;
    } 
    const len = this.ctrlElements?.toArray()?.length ?? 0;
    const isEmpty = len === 0;
    this.isDeviceManagementEmpty.emit(isEmpty);
  }

  showFirmwareSection(): boolean {
    if (!this.firmwareVersions) return false;
  
    if (this.device.deviceType === '31-15') return true;
  
    const pairedWith3115 = this.pairedDevices?.some(p => p.deviceType === '31-15');
    return !pairedWith3115;
  }
  

  ngOnInit(): void {
    if (
      this.device.replaceDevice &&
      this.device.replaceDevice !== "00000000" &&
      this.device.replaceDevice !== "0"
    ) {
      this.replaceDevice = this.device.replaceDevice;
      this.isReplaced = true;
    }
    this.toBeDeleted = this.device.toBeDeleted;
    this.deviceInventoryEnabled = this.helper.isFeatureEnabled("DeviceRegistry");
    if (this.device.deviceType === "32-8") {
      this.api.getLockAndBeaconConfig(this.device.id).subscribe({
        next: (res) => {
          this.currentDoorId = res?.doorId;
          this.doorIdRotationActive = res?.doorIdRotationActive ?? false;
        },
        error: () => {}
      });
    }
  }

  ngAfterViewInit(): void {
    this.ctrlElements.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkElementVisibility());
  }

  private emitVisibility(): void {
    const isEmpty = this.ctrlElements.length === 0;
    this.isDeviceManagementEmpty.emit(isEmpty);
  }

  ngOnChanges(changes: SimpleChanges): void {
     if (changes['device']) {
    Promise.resolve().then(() => this.emitVisibility());
  }
    this.user = JSON.parse(localStorage.getItem("user"));
    this.firmwareVersions = null;

    if (this.user.roleLevel > 100) {
      this.api.getDeviceFirmwareVersions(this.device.id).subscribe(
        (res) => {
          res?.sort(
            (a, b) => parseFloat(b.firmwareId) - parseFloat(a.firmwareId)
          );
          this.firmwareVersions = res;
          this.createOtaUpdateForm();
        },
        (err) => { }
      );
    }

    this.translate.get(["SEARCH_FOR_HUB_OR_DEVICE"]).subscribe((t) => {
      this.basestationSelect.texts = {
        defaultTitle: t.SEARCH_FOR_HUB_OR_DEVICE,
      };
    });

    // Initialize form submit subject with 3 second throttle time to prevent multiple submits
    this.formSubmitSubject.pipe(throttleTime(3000)).subscribe(() => {
      this.submitOta();
    });

    // Initialize basestation select
    this.parseBaseDevOptions(this.basestationList);
  }

  ngOnDestroy(): void {
    this.formSubmitSubject.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}

interface IBaseDeviceSelect {
  texts: IMultiSelectTexts;
  settings: IMultiSelectSettings;
  options: IMultiSelectOption[];
}
